import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slide,
  Fade,
  Snackbar,
  Alert
} from '@mui/material';
import {
  NotificationsNone as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  NotificationsActive as NotificationsActiveIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useNotificacao } from '../../contexts/NotificacaoContext';
import { format, formatDistance } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const CentralNotificacoes = () => {
  const [tabValue, setTabValue] = useState(0);
  const { notificacoes, marcarComoLida, removerNotificacao } = useNotificacao();
  const [deleteDialog, setDeleteDialog] = useState({ open: false, notificacaoId: null });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Separar e ordenar notificações
  const notificacoesOrdenadas = [...notificacoes].sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  );
  const notificacoesNaoLidas = notificacoesOrdenadas.filter(n => !n.lida);
  const notificacoesLidas = notificacoesOrdenadas.filter(n => n.lida);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMarcarComoLida = async (id) => {
    try {
      await marcarComoLida(id);
      setToast({
        open: true,
        message: 'Notificação marcada como lida',
        severity: 'success'
      });
    } catch (error) {
      setToast({
        open: true,
        message: 'Erro ao marcar notificação como lida',
        severity: 'error'
      });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, notificacaoId: id });
  };

  const handleDeleteConfirm = async () => {
    try {
      await removerNotificacao(deleteDialog.notificacaoId);
      setToast({
        open: true,
        message: 'Notificação removida com sucesso',
        severity: 'success'
      });
    } catch (error) {
      setToast({
        open: true,
        message: 'Erro ao remover notificação',
        severity: 'error'
      });
    }
    setDeleteDialog({ open: false, notificacaoId: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, notificacaoId: null });
  };

  const formatarData = (data) => {
    const dataObj = new Date(data);
    const dataFormatada = format(dataObj, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    const tempoRelativo = formatDistance(dataObj, new Date(), { 
      locale: ptBR, 
      addSuffix: true 
    });
    
    return {
      completa: dataFormatada,
      relativa: tempoRelativo
    };
  };

  const renderNotificacao = (notificacao) => (
    <Slide direction="right" in={true} mountOnEnter unmountOnExit key={notificacao.id}>
      <ListItem
        sx={{
          bgcolor: notificacao.lida ? 'background.default' : 'action.hover',
          mb: 1,
          borderRadius: 1,
          transition: 'all 0.3s',
          '&:hover': {
            bgcolor: 'action.selected',
            transform: 'translateX(5px)'
          }
        }}
      >
        <ListItemIcon>
          <Fade in={true}>
            {notificacao.lida ? (
              <NotificationsIcon color="disabled" />
            ) : (
              <NotificationsActiveIcon color="primary" />
            )}
          </Fade>
        </ListItemIcon>
        
        <ListItemText
          primary={
            <Typography variant="subtitle1" component="div">
              {notificacao.titulo}
            </Typography>
          }
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary">
                {notificacao.mensagem}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                <Tooltip title={formatarData(notificacao.createdAt).completa}>
                  <Typography variant="caption" color="text.secondary">
                    {formatarData(notificacao.createdAt).relativa}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          }
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          {!notificacao.lida && (
            <Tooltip title="Marcar como lida">
              <IconButton 
                size="small"
                onClick={() => handleMarcarComoLida(notificacao.id)}
                color="primary"
                sx={{
                  '&:hover': {
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s'
                  }
                }}
              >
                <CheckCircleIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Remover notificação">
            <IconButton 
              size="small"
              onClick={() => handleDeleteClick(notificacao.id)}
              color="error"
              sx={{
                '&:hover': {
                  transform: 'scale(1.1)',
                  transition: 'transform 0.2s'
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </ListItem>
    </Slide>
  );

  return (
    <>
      <Paper elevation={3} sx={{ p: 2, maxWidth: '100%', mx: 'auto' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab 
              label={
                <Badge badgeContent={notificacoesNaoLidas.length} color="primary">
                  Não lidas
                </Badge>
              } 
            />
            <Tab label="Lidas" />
            <Tab label="Todas" />
          </Tabs>
        </Box>

        <List>
          {tabValue === 0 && (
            <>
              {notificacoesNaoLidas.map(renderNotificacao)}
              {notificacoesNaoLidas.length === 0 && (
                <Fade in={true}>
                  <Typography color="text.secondary" align="center" py={4}>
                    Nenhuma notificação não lida
                  </Typography>
                </Fade>
              )}
            </>
          )}
          
          {tabValue === 1 && (
            <>
              {notificacoesLidas.map(renderNotificacao)}
              {notificacoesLidas.length === 0 && (
                <Fade in={true}>
                  <Typography color="text.secondary" align="center" py={4}>
                    Nenhuma notificação lida
                  </Typography>
                </Fade>
              )}
            </>
          )}
          
          {tabValue === 2 && (
            <>
              {notificacoesOrdenadas.map(renderNotificacao)}
              {notificacoesOrdenadas.length === 0 && (
                <Fade in={true}>
                  <Typography color="text.secondary" align="center" py={4}>
                    Nenhuma notificação encontrada
                  </Typography>
                </Fade>
              )}
            </>
          )}
        </List>
      </Paper>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Confirmar exclusão
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir esta notificação?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast de feedback */}
      <Snackbar 
        open={toast.open} 
        autoHideDuration={3000} 
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert 
          onClose={() => setToast({ ...toast, open: false })} 
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CentralNotificacoes;